/**
 * ALBERO product components (product page + quick add):
 *  - <albero-variant-picker>: generic option → variant resolution from Shopify variant data
 *  - <albero-product>: keeps price, media, CTA, URL, sticky bar in sync; submits the real product form
 *  - <albero-gallery>: swipe / arrows / thumbnails gallery
 *  - <albero-quantity>, <albero-deferred-media>, <albero-delivery-estimate>
 */
import { addToCart, config, formatString, announce, prefersReducedMotion } from '@albero/core';

/* -------------------------------------------------------------------------- */
/* Variant picker                                                              */
/* -------------------------------------------------------------------------- */

class AlberoVariantPicker extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[data-variants]');
    try {
      this.variants = JSON.parse(data?.textContent || '[]');
    } catch (error) {
      this.variants = [];
    }
    this.fieldsets = [...this.querySelectorAll('fieldset[data-option-index]')];
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-value]')) this.#onChange(event.target);
    });
    this.refresh(false);
  }

  get selections() {
    return this.fieldsets.map((fieldset) => {
      const select = fieldset.querySelector('select[data-option-value]');
      if (select) return select.value || null;
      const checked = fieldset.querySelector('[data-option-value]:checked');
      return checked ? checked.value : null;
    });
  }

  get missingFieldsets() {
    return this.fieldsets.filter((fieldset) => {
      const select = fieldset.querySelector('select[data-option-value]');
      if (select) return !select.value;
      return !fieldset.querySelector('[data-option-value]:checked');
    });
  }

  get isComplete() {
    return this.missingFieldsets.length === 0;
  }

  get currentVariant() {
    const selections = this.selections;
    if (selections.some((value) => value === null)) return null;
    return this.variants.find((variant) => variant.options.every((value, index) => value === selections[index])) || null;
  }

  /** Selects the values of a given variant (used by colour showcase & deep links). */
  selectVariant(variantId) {
    const variant = this.variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return;
    variant.options.forEach((value, index) => {
      const fieldset = this.fieldsets[index];
      const select = fieldset?.querySelector('select[data-option-value]');
      if (select) {
        select.value = value;
        return;
      }
      const input = [...(fieldset?.querySelectorAll('[data-option-value]') || [])].find(
        (el) => el.value === value
      );
      if (input) input.checked = true;
    });
    this.refresh(true);
  }

  /** Selects one option value by option type/name (e.g. colour showcase). */
  selectValue(optionIndex, value) {
    const fieldset = this.fieldsets[optionIndex];
    const select = fieldset?.querySelector('select[data-option-value]');
    if (select) {
      select.value = value;
      this.refresh(true);
      return;
    }
    const input = [...(fieldset?.querySelectorAll('[data-option-value]') || [])].find(
      (el) => el.value === value
    );
    if (!input) return;
    input.checked = true;
    this.refresh(true);
  }

  #onChange(input) {
    const fieldset = input.closest('fieldset');
    fieldset?.querySelector('[data-option-error]')?.setAttribute('hidden', '');
    fieldset?.classList.remove('has-error');
    this.refresh(true);
  }

  refresh(emit = true) {
    const selections = this.selections;
    this.fieldsets.forEach((fieldset, index) => {
      const current = fieldset.querySelector('[data-option-current]');
      if (selections[index] !== null && current) current.textContent = selections[index];

      const select = fieldset.querySelector('select[data-option-value]');
      if (select) {
        [...select.options].forEach((option) => {
          if (!option.value) return;
          const candidate = [...selections];
          candidate[index] = option.value;
          const matches = this.variants.filter((variant) =>
            variant.options.every((value, i) => candidate[i] === null || candidate[i] === value)
          );
          const exists = matches.length > 0;
          const available = matches.some((variant) => variant.available);
          option.disabled = !exists || !available;
        });
      } else {
        fieldset.querySelectorAll('[data-option-value]').forEach((input) => {
          const candidate = [...selections];
          candidate[index] = input.value;
          const matches = this.variants.filter((variant) =>
            variant.options.every((value, i) => candidate[i] === null || candidate[i] === value)
          );
          const exists = matches.length > 0;
          const available = matches.some((variant) => variant.available);
          const label = fieldset.querySelector(`label[for="${input.id}"]`);
          if (!label) return;
          label.classList.toggle('is-unavailable', !available);
          label.classList.toggle('is-missing', !exists);
          label.querySelector('[data-unavailable-text]')?.toggleAttribute('hidden', available);

          if (fieldset.dataset.optionType === 'pack') {
            const exact = matches.find((variant) => variant.options.every((value, i) => candidate[i] === value)) || matches[0];
            if (exact) {
              const price = label.querySelector('[data-pack-price]');
              const compare = label.querySelector('[data-pack-compare]');
              const note = label.querySelector('[data-pack-note]');
              const badge = label.querySelector('[data-pack-badge]');
              if (price) price.textContent = exact.price;
              if (compare) compare.textContent = exact.compare || '';
              if (note && exact.pack_note) note.textContent = exact.pack_note;
              if (badge) {
                badge.textContent = exact.pack_badge || '';
                badge.toggleAttribute('hidden', !exact.pack_badge);
              }
            }
          }
        });
      }
    });

    if (emit) {
      this.dispatchEvent(
        new CustomEvent('albero:variant-change', {
          bubbles: true,
          detail: { variant: this.currentVariant, complete: this.isComplete, selections },
        })
      );
    }
  }

  showMissingErrors() {
    const missing = this.missingFieldsets;
    missing.forEach((fieldset) => {
      fieldset.classList.add('has-error');
      fieldset.querySelector('[data-option-error]')?.removeAttribute('hidden');
    });
    return missing[0] || null;
  }
}

if (!customElements.get('albero-variant-picker')) customElements.define('albero-variant-picker', AlberoVariantPicker);

/* -------------------------------------------------------------------------- */
/* Product controller                                                          */
/* -------------------------------------------------------------------------- */

class AlberoProduct extends HTMLElement {
  #abort = null;
  #sraController = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.form = document.getElementById(this.dataset.formId);
    this.picker = document.getElementById(this.dataset.pickerId);
    this.addButton = this.querySelector('[data-add-button]');
    this.sticky = this.querySelector('albero-sticky-atc');

    this.addEventListener('albero:variant-change', (event) => this.#onVariantChange(event.detail), { signal });
    this.form?.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
    this.sticky?.querySelector('[data-sticky-button]')?.addEventListener('click', () => this.#onStickyClick(), { signal });
    this.#setupSticky();

    // Colour showcase sections dispatch this to select a value in the main picker.
    document.addEventListener(
      'albero:select-option',
      (event) => {
        if (String(event.detail?.productId) !== String(this.dataset.productId) || !this.picker) return;
        const index = this.picker.fieldsets.findIndex(
          (fieldset) => fieldset.dataset.optionName === event.detail.optionName
        );
        if (index > -1) this.picker.selectValue(index, event.detail.value);
        this.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.stickyObserver?.disconnect();
    document.body.classList.remove('albero-has-sticky-atc');
  }

  get isComplete() {
    return this.picker ? this.picker.isComplete : true;
  }

  get variant() {
    return this.picker ? this.picker.currentVariant : null;
  }

  #onVariantChange({ variant, complete, selections }) {
    const idInput = this.form?.querySelector('[data-variant-id-input]');
    if (variant && idInput) idInput.value = variant.id;

    // Price
    const priceWrapper = this.querySelector('[data-product-price]');
    if (variant && priceWrapper) priceWrapper.innerHTML = variant.price_html;

    this.#updateButton(variant, complete);

    // Media: cambia la foto en cuanto se elige un color, incluso antes de elegir la talla.
    const colorIndex = this.picker?.fieldsets.findIndex((fieldset) => fieldset.dataset.optionType === 'color') ?? -1;
    const selectedColor = colorIndex > -1 ? selections?.[colorIndex] : null;
    const mediaVariant = variant || (
      selectedColor
        ? this.picker?.variants.find((item) => item.options[colorIndex] === selectedColor && item.media_id)
        : null
    );
    if (mediaVariant?.media_id) this.querySelector('albero-gallery')?.goToMedia(mediaVariant.media_id);

    // URL (product page only)
    if (variant && this.picker?.hasAttribute('data-update-url')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    this.#updateSticky(variant, complete);
    if (variant) this.#refreshVariantFragments(variant.id);
  }

  #updateButton(variant, complete) {
    if (!this.addButton) return;
    const label = this.addButton.querySelector('[data-add-label]');
    const price = this.addButton.querySelector('[data-add-price]');
    const defaultLabel = this.querySelector('template[data-add-label-default]')?.content.textContent.trim() || config.strings.addToCart;
    let text = defaultLabel;
    let disabled = false;
    if (complete && !variant) {
      text = config.strings.unavailable;
      disabled = true;
    } else if (variant && !variant.available) {
      text = config.strings.soldOut;
      disabled = true;
    }
    if (label) label.textContent = text;
    if (price && variant) price.textContent = variant.price;
    this.addButton.setAttribute('aria-disabled', String(disabled));
    this.addButton.classList.toggle('is-disabled', disabled);
  }

  /** Re-renders server fragments that depend on the variant (e.g. pickup availability). */
  async #refreshVariantFragments(variantId) {
    const fragments = this.querySelectorAll('[data-variant-sra]');
    if (!fragments.length || !this.dataset.productUrl) return;
    this.#sraController?.abort();
    this.#sraController = new AbortController();
    try {
      const url = `${this.dataset.productUrl}?variant=${variantId}&section_id=${this.dataset.sectionId}`;
      const response = await fetch(url, { signal: this.#sraController.signal });
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      fragments.forEach((fragment) => {
        const fresh = html.querySelector(`[data-variant-sra="${fragment.dataset.variantSra}"]`);
        if (fresh) fragment.innerHTML = fresh.innerHTML;
      });
    } catch (error) {
      /* aborted or offline: keep current content */
    }
  }

  #focusMissing() {
    const first = this.picker?.showMissingErrors();
    if (!first) return;
    const name = first.dataset.optionName || '';
    const error = this.form?.querySelector('[data-form-error]');
    if (error) {
      error.textContent = formatString(config.strings.chooseOption, { option: name.toLowerCase() });
      error.hidden = false;
    }
    first.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    first.querySelector('[data-option-value]')?.focus({ preventScroll: true });
  }

  async #onSubmit(event) {
    event.preventDefault();
    const error = this.form.querySelector('[data-form-error]');
    if (error) error.hidden = true;

    if (!this.isComplete) {
      this.#focusMissing();
      return;
    }

    const variant = this.variant;
    if (this.picker && (!variant || !variant.available)) return;
    if (this.addButton?.getAttribute('aria-disabled') === 'true' && !this.picker) return;
    if (this.addButton?.classList.contains('is-loading')) return;

    const formData = new FormData(this.form);
    const packQty = Number(this.querySelector('[data-pack-qty]:checked')?.dataset.packQty || 1);
    let cartInput = formData;
    let packItems = null;

    if (packQty > 1 && this.picker) {
      const colorIndex = this.picker.fieldsets.findIndex((fieldset) => fieldset.dataset.optionType === 'color');
      const colorSelects = [...this.querySelectorAll('[data-pack-color-select]')];

      if (colorIndex < 0 || colorSelects.length !== packQty) {
        if (error) {
          error.textContent = 'Elige los colores de todas las camisas del pack.';
          error.hidden = false;
        }
        return;
      }

      const baseSelections = this.picker.selections;
      const sellingPlan = formData.get('selling_plan') || '';
      const merged = new Map();

      for (const select of colorSelects) {
        if (!select.value) {
          if (error) {
            error.textContent = 'Elige un color para cada camisa del pack.';
            error.hidden = false;
          }
          select.focus();
          return;
        }

        const candidate = [...baseSelections];
        candidate[colorIndex] = select.value;
        const lineVariant = this.picker.variants.find((item) =>
          item.options.every((value, index) => value === candidate[index])
        );

        if (!lineVariant || !lineVariant.available) {
          if (error) {
            error.textContent = `La camisa en ${select.value} no está disponible con la talla elegida.`;
            error.hidden = false;
          }
          select.focus();
          return;
        }

        const key = String(lineVariant.id);
        const existing = merged.get(key);
        if (existing) {
          existing.quantity += 1;
        } else {
          const line = { id: lineVariant.id, quantity: 1 };
          if (sellingPlan) line.selling_plan = sellingPlan;
          merged.set(key, line);
        }
      }

      packItems = [...merged.values()];
      cartInput = packItems;
    }

    const before = new CustomEvent('albero:before-add', {
      bubbles: true,
      cancelable: true,
      detail: { formData, variant, product: this, packItems },
    });
    if (!this.dispatchEvent(before)) return;

    this.#setLoading(true);
    try {
      await addToCart(cartInput, { source: this, context: this.closest('albero-quick-add') ? 'dialog' : 'product' });
      this.dispatchEvent(
        new CustomEvent('albero:product-added', {
          bubbles: true,
          detail: { variant, packItems },
        })
      );
    } catch (err) {
      if (error) {
        error.textContent = err.message || config.strings.addError;
        error.hidden = false;
      }
      announce(err.message || config.strings.addError);
    } finally {
      this.#setLoading(false);
    }
  }

  #setLoading(loading) {
    [this.addButton, this.sticky?.querySelector('[data-sticky-button]')].forEach((button) => {
      if (!button) return;
      button.classList.toggle('is-loading', loading);
      button.setAttribute('aria-busy', String(loading));
    });
  }

  /* ------------------------------ Sticky bar ----------------------------- */

  #setupSticky() {
    if (!this.sticky || !this.addButton) return;
    this.sticky.hidden = false;
    this.stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', String(!show));
        this.sticky.toggleAttribute('inert', !show);
        document.body.classList.toggle('albero-has-sticky-atc', show);
      },
      { threshold: 0 }
    );
    this.stickyObserver.observe(this.addButton);
    this.sticky.setAttribute('aria-hidden', 'true');
    this.sticky.setAttribute('inert', '');
  }

  #updateSticky(variant, complete) {
    if (!this.sticky) return;
    const meta = this.sticky.querySelector('[data-sticky-meta]');
    const button = this.sticky.querySelector('[data-sticky-button]');
    const image = this.sticky.querySelector('[data-sticky-image]');
    if (variant && meta) {
      meta.textContent = variant.title && variant.options.length && variant.title !== 'Default Title'
        ? `${variant.title} · ${variant.price}`
        : variant.price;
    }
    if (variant?.image && image) {
      image.src = variant.image;
      image.removeAttribute('srcset');
    }
    if (button) {
      if (!complete) {
        const name = this.picker?.missingFieldsets[0]?.dataset.optionName || '';
        button.textContent = formatString(config.strings.chooseCta, { option: name.toLowerCase() });
      }
      else if (!variant) button.textContent = config.strings.unavailable;
      else if (!variant.available) button.textContent = config.strings.soldOut;
      else button.textContent = config.strings.addToCart;
      button.setAttribute('aria-disabled', String(!!(complete && (!variant || !variant.available))));
    }
  }

  #onStickyClick() {
    if (!this.isComplete) {
      this.#focusMissing();
      return;
    }
    this.form?.requestSubmit();
  }
}

if (!customElements.get('albero-product')) customElements.define('albero-product', AlberoProduct);

/* The sticky bar is a plain container styled by CSS; define it so it's not an unknown element. */
if (!customElements.get('albero-sticky-atc')) customElements.define('albero-sticky-atc', class extends HTMLElement {});

/* -------------------------------------------------------------------------- */
/* Gallery                                                                     */
/* -------------------------------------------------------------------------- */

class AlberoGallery extends HTMLElement {
  #index = 0;
  #frame = null;
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('.albero-gallery__slide')];
    this.counter = this.querySelector('[data-gallery-counter]');
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.track) return;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.#index - 1), { signal });
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.#index + 1), { signal });
    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.galleryThumb)), { signal })
    );
    this.track.addEventListener('scroll', () => this.#onScroll(), { passive: true, signal });
    this.track.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.#index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.#index - 1);
        }
      },
      { signal }
    );

    const start = Number(this.dataset.start || 0);
    if (start > 0) window.requestAnimationFrame(() => this.goTo(start, true));
    else this.#setIndex(0);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  goToMedia(mediaId) {
    const index = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
    if (index > -1) this.goTo(index);
  }

  goTo(index, instant = false) {
    if (!this.slides.length) return;
    const count = this.slides.length;
    const next = ((index % count) + count) % count;
    const slide = this.slides[next];
    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior: instant || prefersReducedMotion() ? 'auto' : 'smooth',
    });
    this.#setIndex(next);
  }

  #onScroll() {
    if (this.#frame) return;
    this.#frame = window.requestAnimationFrame(() => {
      this.#frame = null;
      const width = this.track.clientWidth || 1;
      const index = Math.round(this.track.scrollLeft / width);
      if (index !== this.#index) this.#setIndex(Math.min(Math.max(index, 0), this.slides.length - 1));
    });
  }

  #setIndex(index) {
    this.#index = index;
    if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
    this.thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      } else thumb.removeAttribute('aria-current');
    });
    // Pause videos that are no longer visible
    this.slides.forEach((slide, i) => {
      if (i !== index) slide.querySelectorAll('video').forEach((video) => video.pause());
    });
  }
}

if (!customElements.get('albero-gallery')) customElements.define('albero-gallery', AlberoGallery);

/* -------------------------------------------------------------------------- */
/* Quantity                                                                    */
/* -------------------------------------------------------------------------- */

class AlberoQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.#step(-1));
    this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.#step(1));
  }

  #step(direction) {
    const step = Number(this.input.step) || 1;
    const min = Number(this.input.min) || 1;
    const max = this.input.max ? Number(this.input.max) : Infinity;
    const next = Math.min(Math.max((Number(this.input.value) || min) + direction * step, min), max);
    this.input.value = String(next);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('albero-quantity')) customElements.define('albero-quantity', AlberoQuantity);

/* -------------------------------------------------------------------------- */
/* Deferred media (external video, 3D models)                                  */
/* -------------------------------------------------------------------------- */

class AlberoDeferredMedia extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-deferred-trigger]')?.addEventListener('click', () => this.load(), { once: true });
  }

  load() {
    const template = this.querySelector('template');
    if (!template) return;
    const content = template.content.cloneNode(true);
    this.replaceChildren(content);
    if (this.dataset.mediaType === 'model' && window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: () => {} }]);
    }
    this.querySelector('iframe, model-viewer')?.focus?.();
  }
}

if (!customElements.get('albero-deferred-media')) customElements.define('albero-deferred-media', AlberoDeferredMedia);

/* -------------------------------------------------------------------------- */
/* Delivery estimate                                                           */
/* -------------------------------------------------------------------------- */

class AlberoDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const { min, max, businessDays, locale } = config.delivery || {};
    const target = this.querySelector('[data-delivery-range]');
    if (!target || !max) return;
    const add = (days) => {
      const date = new Date();
      let remaining = days;
      while (remaining > 0) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (!businessDays || (day !== 0 && day !== 6)) remaining -= 1;
      }
      return date;
    };
    try {
      const format = new Intl.DateTimeFormat(locale || document.documentElement.lang || undefined, {
        day: 'numeric',
        month: 'short',
      });
      const from = format.format(add(Math.max(Number(min) || 0, 0)));
      const to = format.format(add(Number(max)));
      target.textContent = from === to ? to : `${from} – ${to}`;
    } catch (error) {
      /* keep server text */
    }
  }
}

if (!customElements.get('albero-delivery-estimate')) {
  customElements.define('albero-delivery-estimate', AlberoDeliveryEstimate);
}